import {
  PriceUtils,
  SelectedProposalPackage,
  ServiceProposalPackage,
  ServiceProposalPartAction,
  hasDiscount,
} from "@repo/common"
import { cn } from "@repo/ui/lib/utils"
import { Lock, MessageCircle, Package, Tag, Wrench } from "lucide-react"
import * as React from "react"
import { GiChoice } from "react-icons/gi"
import { useTranslations } from "use-intl"
import { OriginalPrice } from "../../components/original-price"
import {
  SelectableCard,
  SelectableCardContent,
  SelectableCardHeader,
  SelectableCardIcon,
  SelectableCardIndicator,
  SelectableCardPrice,
  SelectableCardTitle,
} from "./selectable-card"

interface ServiceProposalPackageCardProps {
  data: ServiceProposalPackage
  selectedPackage?: SelectedProposalPackage // Optional - if undefined, show full package with recommended choices
  selected: boolean
  standalonePartActions: ServiceProposalPartAction[]
  onSelect: () => void
  interactive?: boolean
  isLocked?: boolean
  className?: string
}

const ServiceProposalPackageCard = React.forwardRef<
  HTMLDivElement,
  ServiceProposalPackageCardProps
>(
  (
    {
      className,
      data,
      selectedPackage,
      selected,
      onSelect,
      interactive = true,
      isLocked = false,
    },
    ref,
  ) => {
    const t = useTranslations("ui")
    // Get active component actions and wash based on selection
    let activeComponentActions = data.partActions
    let activeWash = data.wash
    let productChoiceDecisions: Record<string, string> = {}

    if (selectedPackage) {
      // Package is selected - use actual selections
      const selectedPartIds = selectedPackage.selectedPartActions.map(
        (pa) => pa.partId,
      )
      activeComponentActions = data.partActions.filter((action) =>
        selectedPartIds.includes(action.partId),
      )

      activeWash =
        selectedPackage.selectedWashOriginalId === data.wash?.originalWashId
          ? data.wash
          : null

      // Convert selectedPartActions to productChoiceDecisions
      productChoiceDecisions = {}
      for (const partAction of selectedPackage.selectedPartActions) {
        Object.assign(productChoiceDecisions, partAction.productDecisions)
      }
    } else {
      // Package is NOT selected - show full package with recommended choices
      activeComponentActions = data.partActions
      activeWash = data.wash

      // Use recommended product choices
      productChoiceDecisions = data.partActions.reduce(
        (acc, partAction) => {
          for (const choice of partAction.productChoices) {
            acc[choice.id] = choice.recommendedAlternative.id
          }
          return acc
        },
        {} as Record<string, string>,
      )
    }

    // Calculate total product cost from active component actions
    const totalProductCost = activeComponentActions.reduce((sum, action) => {
      const productChoiceCosts = action.productChoices.reduce(
        (choiceSum, choice) => {
          const selectedProductId = productChoiceDecisions[choice.id]
          let selectedProduct: { price: number } | undefined

          if (selectedProductId) {
            // Find the product by ID among all alternatives
            const allProducts = [
              choice.recommendedAlternative,
              ...choice.otherAlternatives,
            ]
            selectedProduct = allProducts.find(
              (product) => product.id === selectedProductId,
            )
          } else {
            // Default to recommended alternative
            selectedProduct = choice.recommendedAlternative
          }

          return choiceSum + (selectedProduct ? selectedProduct.price : 0)
        },
        0,
      )
      const directProductCosts = action.products.reduce(
        (productSum, product) => {
          return productSum + product.price * (product.quantity ?? 1)
        },
        0,
      )
      return sum + productChoiceCosts + directProductCosts
    }, 0)

    // Package price: base package laborPrice (with discount if applicable) + FULL product costs
    const activeDiscount = hasDiscount(data.laborPriceDiscount)
      ? data.laborPriceDiscount
      : null
    const discountedLaborPrice = activeDiscount
      ? (PriceUtils.getDiscountedPriceOrNull(
          data.laborPrice,
          data.laborPriceDiscount,
        ) ?? data.laborPrice)
      : data.laborPrice
    const packagePrice = discountedLaborPrice + totalProductCost
    const originalPackagePrice = data.laborPrice + totalProductCost

    // Count active actions and comments only
    const actionCount = activeComponentActions.length + (activeWash ? 1 : 0)
    const commentCount = activeComponentActions.filter(
      (action) => action.comment,
    ).length

    // Count product choices in active component actions
    const productChoicesCount = activeComponentActions.reduce(
      (total, action) => total + action.productChoices.length,
      0,
    )

    // Count direct products in active component actions
    const productsCount = activeComponentActions.reduce(
      (total, action) => total + action.products.length,
      0,
    )

    // Total product count (products + productChoices)
    const totalProductCount = productsCount + productChoicesCount

    return (
      <SelectableCard
        ref={ref}
        className={cn(
          className,
          isLocked && "border-secondary-foreground bg-secondary-background",
        )}
        selected={selected}
        onSelect={onSelect}
        interactive={interactive}
        color="primary"
        aria-disabled={isLocked || undefined}
      >
        <SelectableCardHeader>
          <div className="flex items-center gap-2">
            <SelectableCardIcon>
              <Package />
            </SelectableCardIcon>
            <SelectableCardTitle>{data.name}</SelectableCardTitle>
          </div>
          {isLocked ? (
            <div className="ml-auto flex-shrink-0 text-foreground">
              <Lock className="size-4" />
              <span className="sr-only">
                {t("proposalForm.approvedAtReception")}
              </span>
            </div>
          ) : (
            <SelectableCardIndicator />
          )}
        </SelectableCardHeader>

        <SelectableCardContent>
          <div className="flex items-end justify-between">
            <div className="space-y-1">
              <SelectableCardPrice>
                {activeDiscount ? (
                  <span className="flex items-center gap-2">
                    <OriginalPrice>
                      {PriceUtils.format(originalPackagePrice)}*
                    </OriginalPrice>
                    <span className="text-destructive-foreground">
                      {PriceUtils.format(packagePrice)}
                    </span>
                    <span className="text-destructive-foreground text-sm">
                      {t("receipt.discountOnLabor", {
                        discount:
                          PriceUtils.formatDiscountLabel(activeDiscount),
                      })}
                    </span>
                  </span>
                ) : (
                  PriceUtils.format(packagePrice)
                )}
              </SelectableCardPrice>
            </div>

            <div className="flex items-center gap-2 text-sm text-foreground">
              {totalProductCount > 0 && (
                <div className="flex items-center gap-1">
                  <Tag className="size-4" />
                  <span>{totalProductCount}</span>
                </div>
              )}

              {productChoicesCount > 0 && (
                <div className="flex items-center gap-1">
                  <GiChoice className="size-4" />
                  <span>{productChoicesCount}</span>
                </div>
              )}

              <div className="flex items-center gap-1">
                <Wrench className="size-4" />
                <span>{actionCount}</span>
              </div>

              {commentCount > 0 && (
                <div className="flex items-center gap-1">
                  <MessageCircle className="size-4" />
                  <span>{commentCount}</span>
                </div>
              )}
            </div>
          </div>
        </SelectableCardContent>
      </SelectableCard>
    )
  },
)

ServiceProposalPackageCard.displayName = "ServiceProposalPackageCard"

export { ServiceProposalPackageCard, type ServiceProposalPackageCardProps }
